import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { NotFound } from './NotFound';
import { useDocumentMeta } from './useDocumentMeta';

/**
 * Legal pages — Terms / Privacy / Risk Disclosure.
 *
 * Each doc has a fixed list of section ids; the title + body for
 * every section are pulled from `site.legal.<doc>.section_<id>_*`
 * i18n keys. Body keys are arrays (one paragraph per element),
 * read via `returnObjects: true`. Sections render with anchor ids
 * so the table-of-contents can deep-link.
 */

const DOCS = ['terms', 'privacy', 'risk'] as const;
type Doc = (typeof DOCS)[number];

const SECTIONS: Record<Doc, readonly string[]> = {
  terms: ['about', 'eligibility', 'fees', 'funds', 'liability', 'contact'],
  privacy: ['about', 'data', 'use', 'sharing', 'rights', 'contact'],
  risk: ['about', 'market', 'custody', 'tech', 'regulatory', 'no_advice'],
} as const;

function asParagraphs(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((s): s is string => typeof s === 'string');
  }
  if (typeof value === 'string') return [value];
  return [];
}

/**
 * Route entry. Validates the `:doc` param first — unknown values
 * render NotFound (was previously a soft-fallback to Terms, which
 * served a 200 OK with canonical=/legal/terms on /legal/<typo>;
 * Google flagged that as thin/duplicate). The validated `doc` is
 * forwarded to <LegalDoc> so the hooks below run only on a real
 * doc page; mixing the validity check with hooks in one component
 * would either violate the Rules of Hooks or fire useDocumentMeta
 * with fallback values on the 404 path.
 */
export function SiteLegal() {
  const { doc } = useParams();
  if (!DOCS.includes(doc as Doc)) {
    return <NotFound />;
  }
  return <LegalDoc doc={doc as Doc} />;
}

function LegalDoc({ doc }: { doc: Doc }) {
  const { t } = useTranslation();
  const sections = SECTIONS[doc];

  useDocumentMeta({
    title: `${t(`site.legal.${doc}.title`)} — Kit Pod`,
    description: asParagraphs(
      t(`site.legal.${doc}.section_about_body`, { returnObjects: true }),
    )[0],
    canonical: `/legal/${doc}`,
  });

  return (
    <article className="mx-auto max-w-3xl px-5 pb-20 pt-12 sm:px-6 sm:pt-16">
      <p className="pb-eyebrow text-pb-accent">
        {t(`site.legal.${doc}.doc_label`)}
      </p>
      <h1 className="pb-display mt-3 text-[clamp(28px,5.2vw,44px)] leading-[1.08]">
        {t(`site.legal.${doc}.title`)}
      </h1>
      <p className="mt-3 text-[13px] text-tg-hint">
        {t('site.legal.updated_label')} · {t('site.legal.updated_date')}
      </p>

      <nav
        aria-label={t('site.legal.toc_label')}
        className="mt-8 rounded-pb-lg border border-pb-border-soft bg-pb-paper/60 p-4 sm:p-5"
      >
        <p className="pb-eyebrow !text-tg-hint">
          {t('site.legal.toc_label')}
        </p>
        <ol className="mt-3 grid gap-2 text-[14px] sm:grid-cols-2">
          {sections.map((id, i) => (
            <li key={id} className="flex items-baseline gap-2">
              <span className="pb-numeral w-5 shrink-0 text-tg-hint">
                {i + 1}.
              </span>
              <a
                href={`#${id}`}
                className="text-pb-accent transition-colors hover:text-tg-text"
              >
                {t(`site.legal.${doc}.section_${id}_title`)}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-10 space-y-10 sm:mt-12 sm:space-y-12">
        {sections.map((id, i) => {
          const paragraphs = asParagraphs(
            t(`site.legal.${doc}.section_${id}_body`, {
              returnObjects: true,
            }),
          );
          return (
            <section key={id} id={id} className="scroll-mt-24">
              <h2 className="text-[20px] font-semibold leading-snug sm:text-[22px]">
                <span className="pb-numeral mr-2 text-tg-hint">{i + 1}.</span>
                {t(`site.legal.${doc}.section_${id}_title`)}
              </h2>
              <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-tg-text/90">
                {paragraphs.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-pb-border-soft pt-8">
        <Link
          to="/"
          className="pb-btn pb-btn-secondary pb-focus-ring inline-flex"
        >
          {t('site.legal.back')}
        </Link>
        <p className="text-[12px] text-tg-hint">
          {t('site.legal.not_advice_footer')}
        </p>
      </div>
    </article>
  );
}
